import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import UnlockCard from './UnlockCard';
import { pingCreditBalance } from './CreditBalance';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Coach Twin — a per-athlete chat panel that streams responses from the
 * SSE endpoint at /api/coach-twin/{id}/chat. Topic-locked, athlete-aware.
 *
 * The streaming consumer:
 *  - Uses fetch + ReadableStream (so we can send Bearer auth) instead of
 *    EventSource (which doesn't allow custom headers).
 *  - Parses SSE frames manually: event: <type>\ndata: <text>\n\n.
 *  - Renders "start" -> shows a typing indicator; "token" -> appends to
 *    the in-flight assistant message; "done" -> commits the message.
 */
export default function CoachTwin({ athleteId, athleteName, theme = 'dark' }) {
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [inFlight, setInFlight] = useState('');
  const [unlockHint, setUnlockHint] = useState(null);
  const scrollerRef = useRef(null);
  const abortRef = useRef(null);

  // Initial history load
  useEffect(() => {
    if (!athleteId) return;
    (async () => {
      try {
        const res = await fetch(`${API}/coach-twin/${athleteId}/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setHistory(data.messages || []);
      } catch (_) { /* ignore */ }
    })();
  }, [athleteId, token]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [history, inFlight]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || streaming) return;
    setDraft('');
    setHistory((h) => [...h, { role: 'athlete', text, at: new Date().toISOString() }]);
    setStreaming(true);
    setInFlight('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API}/coach-twin/${athleteId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let live = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE frames separated by blank lines.
        const frames = buf.split('\n\n');
        buf = frames.pop() || '';
        for (const frame of frames) {
          const lines = frame.split('\n');
          let event = 'message';
          let data = '';
          for (const ln of lines) {
            if (ln.startsWith('event: ')) event = ln.slice(7).trim();
            else if (ln.startsWith('data: ')) data += ln.slice(6);
          }
          // Re-encode escaped newlines.
          data = data.replace(/\\n/g, '\n');
          if (event === 'token') {
            live += data;
            setInFlight(live);
          } else if (event === 'unlock') {
            try { setUnlockHint(JSON.parse(data)); } catch (_) { /* ignore */ }
          } else if (event === 'done') {
            if (live) {
              setHistory((h) => [
                ...h,
                { role: 'coach', text: live, at: new Date().toISOString() },
              ]);
            }
            setInFlight('');
            pingCreditBalance();
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setHistory((h) => [
          ...h,
          { role: 'coach', text: 'Coach Twin had a hiccup. Try again.', at: new Date().toISOString() },
        ]);
      }
    } finally {
      setStreaming(false);
      setInFlight('');
      abortRef.current = null;
    }
  }, [draft, streaming, athleteId, token]);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!athleteId) return null;

  const suggestions = [
    'How was my batting this week?',
    'Give me one drill for the cover drive.',
    'Why am I mistiming the pull shot?',
    'Should I rest tomorrow?',
  ];

  return (
    <section
      className="rounded-2xl flex flex-col overflow-hidden"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
        border: '1px solid rgba(0,201,167,0.2)',
        height: 460,
      }}
    >
      <header
        className="px-4 py-3 flex items-center gap-2"
        style={{
          background: 'linear-gradient(90deg, rgba(0,166,81,0.15), rgba(0,217,255,0.15))',
          borderBottom: '1px solid rgba(0,201,167,0.15)',
        }}
      >
        <span className="text-lg">🧠</span>
        <div className="flex-1">
          <div className="font-semibold">Coach Twin</div>
          <div className="text-xs opacity-70">
            For {athleteName} · cricket & performance only
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded"
          style={{
            background: streaming ? 'rgba(0,217,255,0.2)' : 'rgba(0,201,167,0.15)',
            color: streaming ? '#00D9FF' : '#00C9A7',
          }}
        >
          {streaming ? 'thinking' : 'live'}
        </span>
      </header>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {history.length === 0 && !inFlight && (
          <div className="opacity-70 text-sm space-y-3">
            <p>
              Ask your Coach Twin anything about your cricket. It already knows your
              last 14 days, your goals, and your training load.
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => setDraft(s)}
                  className="text-xs px-2.5 py-1.5 rounded-full transition-all"
                  style={{
                    background: 'rgba(0,201,167,0.1)',
                    border: '1px solid rgba(0,201,167,0.3)',
                    color: '#00C9A7',
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {history.map((m, i) => (
          <Bubble key={`${m.role}-${i}-${(m.text || '').slice(0, 24)}`} role={m.role} text={m.text} theme={theme} />
        ))}
        {inFlight && <Bubble role="coach" text={inFlight + '▍'} theme={theme} ghost />}
        {unlockHint && (
          <UnlockCard hint={unlockHint} onClose={() => setUnlockHint(null)} />
        )}
      </div>

      <div className="px-3 py-3 border-t" style={{ borderColor: 'rgba(150,160,180,0.15)' }}>
        <div className="flex gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask your coach…"
            rows={1}
            disabled={streaming}
            className="flex-1 px-3 py-2 rounded-lg resize-none outline-none"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(150,160,180,0.25)',
              color: 'inherit',
            }}
          />
          <button
            onClick={send}
            disabled={streaming || !draft.trim()}
            className="px-4 rounded-lg font-semibold"
            style={{
              background: streaming
                ? 'rgba(150,160,180,0.3)'
                : 'linear-gradient(135deg, #00A651, #00D9FF)',
              color: '#fff',
            }}
          >
            Send
          </button>
        </div>
      </div>
    </section>
  );
}

function Bubble({ role, text, theme, ghost }) {
  const isCoach = role === 'coach';
  return (
    <div className={`flex ${isCoach ? 'justify-start' : 'justify-end'}`}>
      <div
        className="max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap"
        style={{
          background: isCoach
            ? 'rgba(0,201,167,0.12)'
            : (theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'),
          border: `1px solid ${isCoach ? 'rgba(0,201,167,0.3)' : 'rgba(150,160,180,0.2)'}`,
          color: 'inherit',
          opacity: ghost ? 0.85 : 1,
        }}
      >
        {text}
      </div>
    </div>
  );
}
